import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Table,
  TreeSelect,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatLeadershipTier } from '@/constants/leadership-tier';
import { formatUserRole } from '@/constants/roles';
import { ADMIN_FILTER_OPTIONS, STATUS_LABELS } from '@/constants/user';
import { UserFormDrawer, type UserFormMode } from '@/pages/admin/users/UserFormDrawer';
import { ApiError } from '@/services/api/client';
import * as departmentsApi from '@/services/api/departments';
import * as usersApi from '@/services/api/users';
import { USER_ROLES } from '@/constants/roles';
import type { UserRole } from '@/types/auth';
import type { DepartmentTreeNode } from '@/types/department';
import type { LeadershipTier, UserRecord, UserStatus } from '@/types/user';
import {
  flattenDepartmentTree,
  flattenDepartmentUsers,
  toParentTreeSelectData,
} from '@/utils/department-tree';

type AdminFilterRole = '' | typeof USER_ROLES.ADMIN | 'BUSINESS';

interface FilterFormValues {
  keyword?: string;
  departmentId?: string;
  role?: AdminFilterRole;
}

export function UserManagementPage() {
  const { message, modal } = App.useApp();
  const [filterForm] = Form.useForm<FilterFormValues>();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterFormValues>({});

  const [departmentTree, setDepartmentTree] = useState<DepartmentTreeNode[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<UserFormMode>('create');
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);

  const departmentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const dept of flattenDepartmentTree(departmentTree)) {
      map.set(dept.id, dept.name);
    }
    return map;
  }, [departmentTree]);

  const departmentFilterTree = useMemo(
    () => toParentTreeSelectData(departmentTree),
    [departmentTree],
  );

  const loadDepartments = useCallback(async () => {
    try {
      const tree = await departmentsApi.fetchDepartmentTree();
      setDepartmentTree(tree);
    } catch {
      // department tree is auxiliary; table still works without names
    }
  }, []);

  const leaderNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of flattenDepartmentUsers(departmentTree)) {
      map.set(user.id, user.name);
    }
    return map;
  }, [departmentTree]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await usersApi.fetchUsers({
        page,
        pageSize,
        keyword: filters.keyword?.trim() || undefined,
        departmentId: filters.departmentId,
        role:
          filters.role === USER_ROLES.ADMIN
            ? USER_ROLES.ADMIN
            : filters.role === 'BUSINESS'
              ? USER_ROLES.EMPLOYEE
              : undefined,
      });
      setUsers(result.items);
      setTotal(result.total);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载用户列表失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [filters, message, page, pageSize]);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleSearch = (values: FilterFormValues) => {
    setFilters(values);
    setPage(1);
  };

  const handleReset = () => {
    filterForm.resetFields();
    setFilters({});
    setPage(1);
  };

  const openCreate = () => {
    setDrawerMode('create');
    setEditingUser(null);
    setDrawerOpen(true);
  };

  const openEdit = (record: UserRecord) => {
    setDrawerMode('edit');
    setEditingUser(record);
    setDrawerOpen(true);
  };

  const handleDisable = (record: UserRecord) => {
    modal.confirm({
      title: '确认停用用户',
      content: `确定停用「${record.name}」吗？将清除姓名、手机号等个人信息，日报、图片与点评记录会保留（显示为「已停用用户#编号」）以便溯源。手机号可再次注册新账号。`,
      okText: '确认停用',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await usersApi.deleteUser(record.id);
          message.success('用户已停用，业务记录已保留');
          await loadUsers();
          void loadDepartments();
        } catch (error) {
          const text = error instanceof ApiError ? error.message : '停用失败';
          message.error(text);
          throw error;
        }
      },
    });
  };

  const columns: ColumnsType<UserRecord> = [
    {
      title: '姓名',
      dataIndex: 'name',
      width: 120,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 130,
    },
    {
      title: '账号类型',
      dataIndex: 'role',
      width: 110,
      render: (role: UserRole) => formatUserRole(role),
    },
    {
      title: '领导身份',
      dataIndex: 'leadershipTier',
      width: 120,
      render: (tier: LeadershipTier) => formatLeadershipTier(tier ?? 'NONE'),
    },
    {
      title: '职务',
      dataIndex: 'jobTitle',
      width: 100,
      render: (value: string | null) => value?.trim() || '—',
    },
    {
      title: '直属领导',
      dataIndex: 'leaderId',
      width: 120,
      render: (leaderId: string | null) => {
        if (!leaderId) {
          return '—';
        }
        return leaderNameMap.get(leaderId) ?? leaderId;
      },
    },
    {
      title: '部门',
      dataIndex: 'departmentId',
      width: 160,
      render: (departmentId: string) => departmentNameMap.get(departmentId) ?? departmentId,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (status: UserStatus) => STATUS_LABELS[status],
    },
    {
      title: '最近登录',
      dataIndex: 'lastLoginAt',
      width: 180,
      render: (value: string | null) => (value ? new Date(value).toLocaleString('zh-CN') : '—'),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDisable(record)}>
            停用
          </Button>
        </Space>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showTotal: (count) => `共 ${count} 条`,
    onChange: (nextPage, nextSize) => {
      setPage(nextPage);
      setPageSize(nextSize);
    },
  };

  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        用户管理
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        管理系统用户账号、角色与组织归属。姓名搜索同时匹配手机号与职务。
      </Typography.Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Form<FilterFormValues> form={filterForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="keyword" label="姓名">
            <Input placeholder="姓名 / 手机号 / 职务" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="departmentId" label="部门">
            <TreeSelect
              allowClear
              placeholder="全部部门"
              style={{ width: 240 }}
              treeData={departmentFilterTree}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
            />
          </Form.Item>
          <Form.Item name="role" label="账号类型">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 160 }}
              options={ADMIN_FILTER_OPTIONS.map((item) => ({
                value: item.value || undefined,
                label: item.label,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                新增用户
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table<UserRecord>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={users}
          pagination={pagination}
          scroll={{ x: 980 }}
        />
      </Card>

      <UserFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        departmentTree={departmentTree}
        editingUser={editingUser}
        onClose={() => setDrawerOpen(false)}
        onSuccess={() => {
          void loadUsers();
          void loadDepartments();
        }}
      />
    </div>
  );
}
